import axios from "axios";

import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

import { toAlias } from "./wizard-helpers";
import { WizardState } from "./wizard-types";

/**
 * Build the create-stack API payload from wizard state.
 *
 * Caller MUST guard that `stackType`, `cloudProvider`, `infraType`, and
 * `selectedTemplate` are non-null before invoking — non-null assertions
 * below are safe because of that runtime guard in `handleSubmit`.
 */
export function buildCreateStackPayload(
  state: WizardState,
  projectAlias: string,
) {
  return {
    name: state.name.trim(),
    alias: toAlias(state.name),
    projectAlias,
    systemType: state.stackType!,
    cloudProvider: state.cloudProvider!,
    infraType: state.infraType!,
    targetId: state.infraTarget?.id,
    templateSlug: state.selectedTemplate!.slug,
    stackConfig: {
      ...(state.selectedTemplate!.stackConfig ?? {}),
      // Embed observability preference so the manifest assembler
      // can inject prometheus + grafana when the stack is first built.
      ...(state.enableObservability ? { _observability: true } : {}),
    },
    services:
      state.configuredServices.length > 0
        ? state.configuredServices
        : undefined,
    region: state.region,
    pipelineMode: state.pipelineMode,
    status: "draft",
  };
}

export async function submitCreateStack(
  payload: ReturnType<typeof buildCreateStackPayload>,
): Promise<{ alias: string }> {
  const baseUrl = apiBaseUrls.BACKEND;
  const response = await axios.post(
    `${baseUrl}${ApplicationEndpoints.CREATE_APPLICATION}`,
    payload,
    {
      withCredentials: true,
    },
  );
  return response.data as { alias: string };
}

export function extractSubmitErrorMessage(err: unknown): string {
  const axiosData = axios.isAxiosError(err)
    ? (err.response?.data as { message?: string; detail?: string })
    : null;
  const axiosMessage =
    axiosData?.detail ??
    axiosData?.message ??
    (axios.isAxiosError(err) ? err.message : null);
  return (
    axiosMessage ??
    (err instanceof Error
      ? err.message
      : "Failed to create stack. Please try again.")
  );
}
